/**
 * Performance HUD.
 *
 * recordFrame() runs every frame and only writes into typed arrays. The DOM
 * is refreshed at most 4 times per second; string formatting allocates, so
 * it stays out of the per-frame path.
 */
import type { TierId } from '../config/tiers';

const WINDOW_FRAMES = 300;
const DOM_INTERVAL_MS = 250;
const GOV_SHOWN = 5;
const GOV_HISTORY = 50;
const FIVE_MIN_MS = 5 * 60 * 1000;

export interface HudStatic {
  backend: string;
  physics: string;
  tier: TierId;
  debrisLive: number;
  debrisCapacity: number;
  benchmarkMs: number | null;
  governorEnabled: boolean;
  gpuTiming: boolean;
}

export interface PerfHud {
  recordFrame(frameMs: number, substeps: number, emaMs: number, nowMs: number): void;
  setStatic(s: Partial<HudStatic>): void;
  noteGovernorChange(from: TierId, to: TierId, emaMs: number, nowMs: number): void;
  /** Current OU gust factor. Stored, shown at the next DOM tick. */
  noteGust(g: number): void;
  /** Per-frame CPU ms, GPU ms (NaN if unavailable), and the governor signal. */
  noteTiming(cpuMs: number, gpuMs: number, signalMs: number): void;
  /** Tier blocked after a failed upgrade (or null) and frames until it lifts. */
  noteBlocked(tier: TierId | null, framesLeft: number): void;
  /** Governor's smoothed dropped-frame rate (0 to 1). */
  noteDropRate(rate: number): void;
  /** Cloud fluid worker: ms per step and grid size. */
  noteFluid(ms: number, n: number): void;
  setVisible(v: boolean): void;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  parent: HTMLElement,
  className?: string,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (className) e.className = className;
  parent.appendChild(e);
  return e;
}

export function createPerfHud(root: HTMLElement): PerfHud {
  root.classList.add('hud');
  const header = el('button', root, 'hud-header');
  header.type = 'button';
  header.textContent = 'Performance ▾';
  const body = el('div', root, 'hud-body');
  header.addEventListener('click', () => {
    body.hidden = !body.hidden;
    header.textContent = body.hidden ? 'Performance ▸' : 'Performance ▾';
  });

  const stats = el('pre', body, 'hud-stats');
  const govTitle = el('div', body, 'hud-sub');
  govTitle.textContent = 'Governor (last 5)';
  const govList = el('pre', body, 'hud-gov');
  const copyBtn = el('button', body, 'hud-copy');
  copyBtn.type = 'button';
  copyBtn.textContent = 'Copy bench row';

  const frames = new Float32Array(WINDOW_FRAMES);
  const scratch = new Float32Array(WINDOW_FRAMES);
  let head = 0;
  let filled = 0;
  let lastFrameMs = 0;
  let lastEmaMs = 0;
  let lastSubsteps = 0;
  let lastGust = Number.NaN;
  let lastCpu = 0;
  let lastGpu = Number.NaN;
  let lastSignal = 0;
  let blockedTier: TierId | null = null;
  let blockedFrames = 0;
  let dropRate = 0;
  let fluidMs = 0;
  let fluidN = 0;
  let lastDomMs = -Infinity;
  const startMs = performance.now();

  const info: HudStatic = {
    backend: '?',
    physics: '?',
    tier: 'medium',
    debrisLive: 0,
    debrisCapacity: 0,
    benchmarkMs: null,
    governorEnabled: true,
    gpuTiming: false,
  };

  const govEntries: { tMs: number; from: TierId; to: TierId; emaMs: number }[] = [];
  const govTimes = new Float64Array(GOV_HISTORY);
  let govTimesHead = 0;
  let govTimesCount = 0;

  function windowStats(): { median: number; p95: number } {
    if (filled === 0) return { median: NaN, p95: NaN };
    scratch.set(frames.subarray(0, filled));
    const sorted = scratch.subarray(0, filled).sort();
    const at = (q: number): number =>
      sorted[Math.min(filled - 1, Math.floor(q * filled))] as number;
    return { median: at(0.5), p95: at(0.95) };
  }

  function changesInLastFiveMin(nowMs: number): number {
    let c = 0;
    for (let i = 0; i < govTimesCount; i++) {
      if (nowMs - (govTimes[i] as number) <= FIVE_MIN_MS) c++;
    }
    return c;
  }

  function gpuText(): string {
    if (!info.gpuTiming) return 'n/a (no timer)';
    return Number.isNaN(lastGpu) ? 'pending' : `${lastGpu.toFixed(2)} ms`;
  }

  function refreshDom(nowMs: number): void {
    const { median, p95 } = windowStats();
    const bench = info.benchmarkMs === null ? 'pending' : `${info.benchmarkMs.toFixed(2)} ms`;
    stats.textContent =
      `backend    ${info.backend === 'webgpu' ? 'WebGPU' : info.backend === 'webgl2' ? 'WebGL2' : info.backend}\n` +
      `physics    ${info.physics}\n` +
      `frame      ${lastFrameMs.toFixed(2)} ms\n` +
      `cpu        ${lastCpu.toFixed(2)} ms\n` +
      `gpu        ${gpuText()}\n` +
      `signal     ${lastSignal.toFixed(2)} ms\n` +
      `drops      ${(dropRate * 100).toFixed(1)}% (limit 5%)\n` +
      `fluid      ${fluidN > 0 ? `${fluidMs.toFixed(1)} ms/step, ${fluidN}² (worker)` : 'starting'}\n` +
      `ema        ${lastEmaMs.toFixed(2)} ms\n` +
      `median     ${median.toFixed(2)} ms  (last ${filled})\n` +
      `p95        ${p95.toFixed(2)} ms\n` +
      `tier       ${info.tier}${info.governorEnabled ? '' : ' (manual)'}\n` +
      `blocked    ${blockedTier === null ? 'none' : `${blockedTier} (${Math.ceil(blockedFrames / 60)} s)`}\n` +
      `debris     ${info.debrisLive} / ${info.debrisCapacity}\n` +
      `substeps   ${lastSubsteps}\n` +
      `gust       ${Number.isNaN(lastGust) ? 'off' : lastGust.toFixed(2)}\n` +
      `benchmark  ${bench}`;
    govList.textContent =
      govEntries.length === 0
        ? '(no changes)'
        : govEntries
            .map(
              (g) =>
                `${((g.tMs - startMs) / 1000).toFixed(1)}s  ${g.from} → ${g.to}  ema ${g.emaMs.toFixed(1)}`,
            )
            .join('\n');
    lastDomMs = nowMs;
  }

  copyBtn.addEventListener('click', () => {
    const { median, p95 } = windowStats();
    const now = performance.now();
    const elapsed = now - startMs;
    const changes = elapsed >= FIVE_MIN_MS ? String(changesInLastFiveMin(now)) : '';
    const note = elapsed >= FIVE_MIN_MS ? `${filled}-frame window` : 'run shorter than 5 min';
    // Device, OS, and browser are left for the person running the bench to fill in.
    const row = `| ${new Date().toISOString().slice(0, 10)} |  |  |  | ${info.backend} | ${info.tier} | ${info.debrisLive} | ${median.toFixed(2)} | ${p95.toFixed(2)} | ${changes} | ${note} |`;
    navigator.clipboard.writeText(row).then(
      () => {
        copyBtn.textContent = 'Copied';
      },
      () => {
        copyBtn.textContent = 'Copy failed (see console)';
        console.info(row);
      },
    );
  });

  return {
    recordFrame(frameMs: number, substeps: number, emaMs: number, nowMs: number): void {
      frames[head] = frameMs;
      head = (head + 1) % WINDOW_FRAMES;
      if (filled < WINDOW_FRAMES) filled++;
      lastFrameMs = frameMs;
      lastEmaMs = emaMs;
      lastSubsteps = substeps;
      if (nowMs - lastDomMs >= DOM_INTERVAL_MS) refreshDom(nowMs);
    },
    setStatic(s: Partial<HudStatic>): void {
      Object.assign(info, s);
    },
    noteGovernorChange(from: TierId, to: TierId, emaMs: number, nowMs: number): void {
      govEntries.push({ tMs: nowMs, from, to, emaMs });
      if (govEntries.length > GOV_SHOWN) govEntries.shift();
      govTimes[govTimesHead] = nowMs;
      govTimesHead = (govTimesHead + 1) % GOV_HISTORY;
      if (govTimesCount < GOV_HISTORY) govTimesCount++;
      info.tier = to;
    },
    setVisible(v: boolean): void {
      root.hidden = !v;
    },
    noteGust(g: number): void {
      lastGust = g;
    },
    noteFluid(ms: number, n: number): void {
      fluidMs = ms;
      fluidN = n;
    },
    noteDropRate(rate: number): void {
      dropRate = rate;
    },
    noteBlocked(tier: TierId | null, framesLeft: number): void {
      blockedTier = tier;
      blockedFrames = framesLeft;
    },
    noteTiming(cpuMs: number, gpuMs: number, signalMs: number): void {
      lastCpu = cpuMs;
      lastGpu = gpuMs;
      lastSignal = signalMs;
    },
  };
}
